<template>
  <listing-layout>
    <h1 slot="heading" class="h2">Titles - Preview Standings</h1>
    <div slot="content">
      <div class="alert alert-success" v-if="success">{{success}}</div>
      <div class="col-12 px-0 mt-2">
        <form novalidate class="needs-validation my-3" @submit.prevent="getStandings">
          <div class="row">
            <div class="col-12 col-md-4 form-group">
              <label for="title-type">Please select a title to begin</label>
              <app-multiselect
                id="title-type"
                :value="titleOptions.find(o => o.value === title)"
                :options="titleOptions"
                label="label"
                track-by="value"
                placeholder="-- Select Title --"
                @input="onTitleChange"
              />
            </div>
            <div class="col-12 col-md-4">
              <season-select v-model="year" />
            </div>
            <div v-if="title && title != 5" class="col-12 col-md-4">
              <zone-select v-model="zone" @loaded="zones = $event" />
            </div>
          </div>
          <div class="row">
            <div class="col-12">
              <button type="submit" class="btn btn-primary btn-block-mob mb-3 mb-lg-0 ml-lg-auto" :disabled="!title">
                <div v-if="processing" class="d-flex align-items-center">
                  <div class="spinner-border spinner-border-sm text-light mr-2" role="status"></div>Loading
                </div>
                <span v-else-if="title">Load Standings</span>
                <span v-else>Select a title to proceed</span>
              </button>
            </div>
          </div>
        </form>
      </div>

      <div v-if="title !== 5">
        <div class="mb-5" v-if="rows.length > 0">
          <data-table :dataRows="rows" :dataColumns="columns" :options="options">
            <tableTextTemplate slot-scope="{ row }" slot="expandedHeader" :text="`${row.place}: ${row.rider_name || row.horse_name}`" :row="row" :options="{ expandMobile: true }" />
            <div slot-scope="{ row }" slot="expandedRow">
              <ul class="list-unstyled mb-0 pt-2">
                <li class="pt-2">
                  Position: <tableTextTemplate :text="row.place" :row="row" :options="{inline: true}" />
                </li>
                <li class="pt-2">
                  {{rows.length ? rows[0].hasOwnProperty("rider_name") ? 'Rider' : 'Horse' : ''}}: <tableTextTemplate :text="row.horse_name" :row="row" :options="{inline: true}" />
                </li>
                <li class="pt-2">
                  Owner: <tableTextTemplate :text="row.owner" :row="row" :options="{inline: true}" />
                </li>
                <li class="pt-2">
                  Points: <tableTextTemplate :text="row.total" :row="row" :options="{inline: true}" />
                </li>
                <li class="pt-2">
                  Zone: <tableTextTemplate :text="zones.filter(zone => zone.id === (row.rider_zone || row.horse_zone))[0].code" :row="row" :options="{inline: true}" />
                </li>
              </ul>
            </div>
          </data-table>
        </div>
        <div v-else-if="options.loaded" class="alert alert-warning rounded-0 mb-0">There are no results.</div>
      </div>
      <div v-else-if="title === 5">
        <div class="mb-5" v-if="Object.keys(rows).length > 0">
          <data-table :dataRows="rows" :dataColumns="columns" :options="options">
            <tableTextTemplate slot-scope="{ row }" slot="expandedHeader" :text="`${row.place}: ${row.sire.name}`" :row="row" :options="{ expandMobile: true }" />
            <div slot-scope="{ row }" slot="expandedRow">
              <ul class="list-unstyled mb-0 pt-2">
                <li class="pt-2">
                  Position: <tableTextTemplate :text="row.place" :row="row" :options="{inline: true}" />
                </li>
                <li class="pt-2">
                  Owner: <tableTextTemplate :text="row.owner.name" :row="row" :options="{inline: true}" />
                </li>
                <li class="pt-2">
                  Horse: <tableTextTemplate :text="row.sire.name" :row="row" :options="{inline: true}" />
                </li>
                <li class="pt-2">
                  Points: <tableTextTemplate :text="row.total_points.toFixed(2)" :row="row" :options="{inline: true}" />
                </li>
              </ul>
            </div>
          </data-table>
        </div>
        <div v-else-if="options.loaded" class="alert alert-warning rounded-0 mb-0">There are no results.</div>
      </div>


    </div>
  </listing-layout>
</template>

<script>
/* Use the "slot" attribute to match the heading and content
* to their respective fields in the ListingLayout component. */

import axios from 'axios'
import listingLayout from '@/components/ListingLayout'
import dataTable from '@/components/DataTable'
import tableTextTemplate from '@/components/DataTableFields/Text'
import tableButtonTemplate from '@/components/DataTableFields/Button'
import SeasonSelect from '@/components/SeasonSelect'
import ZoneSelect from '@/components/ZoneSelect'
import AppMultiselect from '@/components/AppMultiselect'

export default {
  name: 'TitlesPreview',
  props: ['success'],
  components: {
    'listing-layout': listingLayout,
    'data-table': dataTable,
    tableTextTemplate,
    tableButtonTemplate,
    SeasonSelect,
    ZoneSelect,
    AppMultiselect
  },
  data() {
    return {
      columns: [],
      columnsNotSire: [
        {
          label: 'Pos',
          headerClass: 'd-none d-lg-table-cell',
          sortable: true,
          filterable: true,
          visible: true,
          component: ({
            props: ['row'],
            components: { tableTextTemplate },
            template: `<tableTextTemplate :text="String(row.place)" :row="row" />`
          }),
          representedAs(row) { return `${row.place}` }
        },
        {
          label: this.rows ? this.rows[0].hasOwnProperty("rider_name") ? 'Rider' : 'Horse' : '',
          headerClass: 'd-none d-lg-table-cell',
          sortable: true,
          filterable: true,
          visible: true,
          component: ({
            props: ['row'],
            components: { tableTextTemplate },
            template: `<tableTextTemplate :text="row.rider_name || row.horse_name" :row="row" />`
          }),
          representedAs(row) { return `${row.rider_name || row.horse_name}` }
        },
        {
          label: 'Owner',
          headerClass: 'd-none d-lg-table-cell',
          sortable: true,
          filterable: true,
          visible: true,
          component: ({
            props: ['row'],
            components: { tableTextTemplate },
            template: `<tableTextTemplate :text="row.owner" :row="row" />`
          }),
          representedAs(row) { return `${row.owner}` }
        },
        {
          label: 'Points',
          headerClass: 'd-none d-lg-table-cell',
          sortable: true,
          filterable: true,
          visible: true,
          component: ({
            props: ['row'],
            components: { tableTextTemplate },
            template: `<tableTextTemplate :text="row.total" :row="row" />`
          }),
          representedAs(row) { return `${row.total}` }
        },
        {
          label: 'Zone',
          headerClass: 'd-none d-lg-table-cell',
          sortable: true,
          filterable: true,
          visible: true,
          component: ({
            props: ['row'],
            components: { tableTextTemplate },
            template: `<tableTextTemplate :text="rowZone(row.rider_zone || row.horse_zone)" :row="row" />`,
            methods: {
              rowZone(zoneID) {
                return this.$store.state.listZones && zoneID ? this.$store.state.listZones.filter(zone => zone.id === zoneID)[0].code : ''
              }
            }
          })
        }
      ],
      columnsSire: [
        {
          label: 'Pos',
          headerClass: 'd-none d-lg-table-cell',
          sortable: true,
          filterable: true,
          visible: true,
          component: ({
            props: ['row'],
            components: { tableTextTemplate },
            template: `<tableTextTemplate :text="String(row.place)" :row="row" />`
          }),
          representedAs(row) { return `${row.place}` }
        },
        {
          label: 'Horse',
          headerClass: 'd-none d-lg-table-cell',
          sortable: true,
          filterable: true,
          visible: true,
          component: ({
            props: ['row'],
            components: { tableTextTemplate },
            template: `<tableTextTemplate :text="row.sire.name" :row="row" />`
          }),
          representedAs(row) { return `${row.sire.name}` }
        },
        {
          label: 'Owner',
          headerClass: 'd-none d-lg-table-cell',
          sortable: true,
          filterable: true,
          visible: true,
          component: ({
            props: ['row'],
            components: { tableTextTemplate },
            template: `<tableTextTemplate :text="row.owner.name" :row="row" />`
          }),
          representedAs(row) { return `${row.owner.name}` }
        },
        {
          label: 'Points',
          headerClass: 'd-none d-lg-table-cell',
          sortable: true,
          filterable: true,
          visible: true,
          component: ({
            props: ['row'],
            components: { tableTextTemplate },
            template: `<tableTextTemplate :text="row.total_points.toFixed(2)" :row="row" />`
          }),
          representedAs(row) { return `${row.total_points.toFixed(2)}` }
        }
      ],
      rows: [],
      status: '',
      error: '',
      year: 0,
      title: '',
      titles: [],
      processing: false,
      options: {
        loaded: false
      },
      zone: 0,
      zones: []
    }
  },
  computed: {
    titleOptions() {
      return this.titles.map(title => ({ label: title.name, value: title.id }))
    }
  },
  mounted() {
    axios.get(`${this.$api}/title-list`).then((response) => {
      if (response.data.status === 'success') {
        this.error = ''
        this.titles = response.data.data
      } else {
        this.error = 'Unable to receive data from the server. Please reload the page.'
      }
    })

    const { title, year, zone } = this.$route.query
    if (title) {
      this.title = Number(title)
      if (year) this.year = Number(year)
      if (zone) this.zone = Number(zone)
      this.getStandings()
    }
  },
  methods: {
    onTitleChange(option) {
      this.title = option ? option.value : ''
      this.rows = []
      this.options.loaded = false
    },
    getZoneLength(zoneID) {
      return this.zones.length > 0 ? this.zones.filter(zone => zone.id === zoneID).length : ''
    },
    getZone(zoneID) {
      return this.getZoneLength(zoneID) > 0 ? this.zones.filter(zone => zone.id === zoneID)[0].name : ''
    },
    getStandings() {
      this.processing = true

      const query = { title: String(this.title), year: String(this.year) }
      if (this.zone && this.title !== 5) query.zone = String(this.zone)
      this.$router.replace({ query }).catch(() => {})

      let url = `${this.$api}/preview-results-title/${this.year}/${this.title}`

      if (this.zone && this.title !== 5) {
        url += `/${this.zone}`
      }

      axios.get(url).then((response) => {
        this.processing = false
        if (response.data.status === 'success') {
          this.options.loaded = true
          this.error = ''
          if (this.title === 5) {
            this.columns.splice(0, this.columns.length, ...this.columnsSire)
            this.rows = response.data.data.results.map((row, index) => {
              const temp = row
              temp.place = index + 1
              return temp
            })
          } else if (Object.keys(response.data.data).length > 0 && response.data.data.results.length > 0) {
            const temp = [].concat(this.columnsNotSire)

            // If Rider titles
            if (this.title > 5) {
              temp.splice(2, 1)
            }

            this.columns.splice(0, this.columns.length, ...temp)
            this.rows = response.data.data.results
            this.setColumnName()
          } else {
            this.rows = []
          }
        } else {
          this.error = 'Unable to receive data from the server. Please reload the page.'
        }
      })
    },
    setColumnName() {
      if (this.columns[1].label === '' || this.columns[1].label === 'Rider' || this.columns[1].label === 'Horse') {
        this.columns[1].label = this.rows[0].hasOwnProperty("rider_name") ? 'Rider' : 'Horse'
      }
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/views/Titles/Preview/TitlesPreview.vue